import { type ReactNode } from 'react'
import { Link } from 'react-router'
import { Logo } from '#app/components/ui/logo.tsx'
import { cn } from '#app/utils/misc.tsx'

/**
 * Editorial dark side panel reused across split-screen auth pages.
 * On mobile (<lg) the panel is hidden — only the form side is shown.
 */
function EditorialAside({
	eyebrow,
	headline,
	headlineItalic,
	body,
	bullets,
}: {
	eyebrow: string
	headline: string
	headlineItalic?: string
	body?: string
	bullets?: string[]
}) {
	return (
		<aside
			className="relative hidden flex-col justify-between overflow-hidden text-white lg:flex"
			style={{
				background: 'var(--ink)',
				padding: '56px 56px 48px',
			}}
		>
			{/* Ambient violet glow (mirrors marketing hero) */}
			<div
				className="pointer-events-none absolute left-1/2 top-[-25%] -translate-x-1/2"
				style={{
					width: 720,
					height: 600,
					background:
						'radial-gradient(ellipse at center, rgba(93, 47, 240, 0.32) 0%, rgba(93, 47, 240, 0) 65%)',
				}}
			/>
			{/* Faint editorial grid */}
			<div
				className="pointer-events-none absolute inset-0"
				style={{
					backgroundImage: `
						linear-gradient(to right, rgba(255,255,255,0.025) 1px, transparent 1px),
						linear-gradient(to bottom, rgba(255,255,255,0.025) 1px, transparent 1px)
					`,
					backgroundSize: '80px 80px',
					maskImage:
						'radial-gradient(ellipse at center, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 75%)',
					WebkitMaskImage:
						'radial-gradient(ellipse at center, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 75%)',
				}}
			/>

			<div className="relative z-10">
				<Logo size="sm" color="white" />
			</div>

			<div className="relative z-10 max-w-105">
				<div
					className="mb-6 inline-flex items-center gap-2.5 uppercase"
					style={{
						fontFamily: 'var(--font-claire-mono)',
						fontSize: 11,
						letterSpacing: '0.18em',
						color: '#a89dff',
					}}
				>
					<span
						aria-hidden="true"
						style={{
							width: 24,
							height: 1,
							background: 'currentColor',
							opacity: 0.6,
						}}
					/>
					{eyebrow}
				</div>
				<h2
					style={{
						fontFamily: 'var(--font-claire-display)',
						fontWeight: 400,
						fontSize: 'clamp(32px, 3.2vw, 44px)',
						lineHeight: 1.05,
						letterSpacing: '-0.025em',
						color: '#fff',
					}}
				>
					{headline}
					{headlineItalic ? (
						<>
							{' '}
							<span
								style={{
									fontStyle: 'italic',
									fontWeight: 300,
									color: '#a89dff',
								}}
							>
								{headlineItalic}
							</span>
						</>
					) : null}
				</h2>
				{body ? (
					<p
						className="mt-6"
						style={{
							fontSize: 16,
							lineHeight: 1.6,
							color: 'rgba(255,255,255,0.7)',
						}}
					>
						{body}
					</p>
				) : null}
			</div>

			<div className="relative z-10 flex flex-col gap-2.5">
				{bullets?.map((bullet) => (
					<span
						key={bullet}
						className="inline-flex items-center gap-2"
						style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)' }}
					>
						<svg width="14" height="14" viewBox="0 0 14 14" fill="none">
							<path
								d="M2.5 7.5L5.5 10.5L11.5 4"
								stroke="#7B5CFF"
								strokeWidth="1.6"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
						{bullet}
					</span>
				))}
				<span
					className="mt-3 uppercase"
					style={{
						fontFamily: 'var(--font-claire-mono)',
						fontSize: 10.5,
						letterSpacing: '0.14em',
						color: 'rgba(255,255,255,0.35)',
					}}
				>
					© Vigilio · Veille médiatique
				</span>
			</div>
		</aside>
	)
}

/**
 * Split-screen auth layout: dark editorial panel (left) + form card (right).
 * Used by login & signup. Falls back to single column on mobile.
 */
export function AuthSplit({
	aside,
	children,
}: {
	aside: {
		eyebrow: string
		headline: string
		headlineItalic?: string
		body?: string
		bullets?: string[]
	}
	children: ReactNode
}) {
	return (
		<div className="grid min-h-screen grid-cols-1 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
			<EditorialAside {...aside} />
			<main
				className="relative flex flex-col items-center justify-center px-5 py-12 sm:px-10"
				style={{ background: 'var(--surface)' }}
			>
				<div className="w-full max-w-115">
					{/* Mobile-only logo (aside is hidden) */}
					<div className="mb-8 flex justify-center lg:hidden">
						<Logo size="default" />
					</div>
					{children}
				</div>
			</main>
		</div>
	)
}

/**
 * Centered editorial card layout. Used by verify, forgot/reset password,
 * and the onboarding flow. Hosts a single column form on a parchment surface.
 */
export function AuthCenter({
	children,
	maxWidth = 460,
	withLogo = true,
}: {
	children: ReactNode
	maxWidth?: number
	withLogo?: boolean
}) {
	return (
		<div
			className="flex min-h-screen flex-col items-center justify-center px-5 py-12 sm:px-10"
			style={{ background: 'var(--surface)' }}
		>
			{withLogo ? (
				<div className="mb-10">
					<Logo size="default" />
				</div>
			) : null}
			<div className="w-full" style={{ maxWidth }}>
				{children}
			</div>
		</div>
	)
}

/**
 * Editorial card wrapper for the form area. Soft border, subtle shadow.
 * Sits on the parchment background.
 */
export function AuthCard({
	eyebrow,
	title,
	titleItalic,
	subtitle,
	children,
	footer,
}: {
	eyebrow?: string
	title: string
	titleItalic?: string
	subtitle?: ReactNode
	children: ReactNode
	footer?: ReactNode
}) {
	return (
		<div
			className="overflow-hidden"
			style={{
				background: '#fff',
				border: '1px solid var(--border-soft)',
				borderRadius: 'var(--r-lg)',
				boxShadow: 'var(--shadow-1)',
			}}
		>
			<div
				className="px-7 pt-8 pb-6 sm:px-9 sm:pt-10"
				style={{ borderBottom: '1px solid var(--border-soft)' }}
			>
				{eyebrow ? (
					<div
						className="mb-4 inline-flex items-center gap-2.5 uppercase"
						style={{
							fontFamily: 'var(--font-claire-mono)',
							fontSize: 11,
							letterSpacing: '0.18em',
							color: 'var(--brand)',
						}}
					>
						<span
							aria-hidden="true"
							style={{
								width: 22,
								height: 1,
								background: 'currentColor',
								opacity: 0.6,
							}}
						/>
						{eyebrow}
					</div>
				) : null}
				<h1
					style={{
						fontFamily: 'var(--font-claire-display)',
						fontWeight: 400,
						fontSize: 30,
						lineHeight: 1.1,
						letterSpacing: '-0.025em',
						color: 'var(--ink)',
					}}
				>
					{title}
					{titleItalic ? (
						<>
							{' '}
							<span
								style={{
									fontStyle: 'italic',
									fontWeight: 300,
									color: 'var(--text-secondary)',
								}}
							>
								{titleItalic}
							</span>
						</>
					) : null}
				</h1>
				{subtitle ? (
					<p
						className="mt-3"
						style={{
							fontSize: 14.5,
							lineHeight: 1.55,
							color: 'var(--text-secondary)',
						}}
					>
						{subtitle}
					</p>
				) : null}
			</div>
			<div className="px-7 py-8 sm:px-9">{children}</div>
			{footer ? (
				<div
					className="px-7 py-5 text-center sm:px-9"
					style={{
						borderTop: '1px solid var(--border-soft)',
						background: 'var(--mist)',
						fontSize: 13.5,
						color: 'var(--text-secondary)',
					}}
				>
					{footer}
				</div>
			) : null}
		</div>
	)
}

/**
 * Editorial 3-step indicator for the onboarding flow.
 * Mono eyebrows (01/02/03) with brand violet underline on the active step.
 */
type OnboardingStep = 1 | 2 | 3

const ONBOARDING_STEPS: { id: OnboardingStep; label: string }[] = [
	{ id: 1, label: 'Profil' },
	{ id: 2, label: 'Organisation' },
	{ id: 3, label: 'Abonnement' },
]

export function OnboardingSteps({
	active,
	// Invitees join an existing organization, so the subscription step does
	// not apply to them — pass lastStep={2} to drop it from the progress bar.
	lastStep = 3,
}: {
	active: OnboardingStep
	lastStep?: OnboardingStep
}) {
	return (
		<ol
			className="mb-8 flex items-stretch justify-center gap-3 sm:gap-5"
			aria-label="Progression"
		>
			{ONBOARDING_STEPS.filter((step) => step.id <= lastStep).map((step) => {
				const isActive = step.id === active
				const isDone = step.id < active
				return (
					<li
						key={step.id}
						className="flex flex-1 flex-col gap-2"
						style={{ maxWidth: 160 }}
						aria-current={isActive ? 'step' : undefined}
					>
						<span
							className="block h-[3px] rounded-full"
							style={{
								background: isActive
									? 'var(--brand)'
									: isDone
										? 'var(--brand-soft)'
										: 'var(--border-soft)',
							}}
						/>
						<div className="flex items-baseline gap-2">
							<span
								className="uppercase"
								style={{
									fontFamily: 'var(--font-claire-mono)',
									fontSize: 10.5,
									letterSpacing: '0.14em',
									color: isActive
										? 'var(--brand)'
										: isDone
											? 'var(--ink)'
											: 'var(--text-secondary)',
									fontWeight: 600,
								}}
							>
								{String(step.id).padStart(2, '0')}
							</span>
							<span
								className={cn(
									'text-[12.5px] sm:text-[13px]',
									isActive ? 'font-medium' : '',
								)}
								style={{
									color: isActive
										? 'var(--ink)'
										: isDone
											? 'var(--ink)'
											: 'var(--text-secondary)',
								}}
							>
								{step.label}
							</span>
						</div>
					</li>
				)
			})}
		</ol>
	)
}

/**
 * Inline link styled to match the editorial system (subtle underline, brand hover).
 */
export function AuthInlineLink({
	to,
	children,
}: {
	to: string
	children: ReactNode
}) {
	return (
		<Link
			to={to}
			className="font-medium transition-colors"
			style={{ color: 'var(--brand)' }}
		>
			{children}
		</Link>
	)
}
